import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserManager } from "./userManager";
import { asError, eventBus } from "@optimyl/events";
import { Spinner } from "../components/Spinner";

/**
 * OIDC callback handler. Processes the authorization response
 * from the Identity Server and redirects to the intake form.
 */
export function CallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const handledRef = useRef(false);

  useEffect(() => {
    // Prevent double-execution from React StrictMode
    if (handledRef.current) return;
    handledRef.current = true;

    // Restore the pre-login URL (with groupId) from sessionStorage
    const raw = sessionStorage.getItem("preLoginUrl");
    const savedUrl = raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : null;

    getUserManager()
      .signinRedirectCallback()
      .then(() => {
        sessionStorage.removeItem("preLoginUrl");
        navigate(savedUrl || "/intake", { replace: true });
      })
      .catch(async (err) => {
        const error = asError(err);

        // "No matching state" means the callback was already processed
        // (e.g. user clicked back) or session was cleared mid-flow.
        // Redirect silently instead of showing an error.
        if (error.message?.includes("No matching state")) {
          const user = await getUserManager().getUser();
          if (user && !user.expired) {
            navigate(savedUrl || "/intake", { replace: true });
          } else {
            navigate("/", { replace: true });
          }
          return;
        }

        eventBus.publish("error", { error, context: "oidc.callback" });
        setError(error.message || "Authentication failed");
      });
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-sm w-full bg-white rounded-xl shadow-md p-8 text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Authentication Error
          </h2>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <a
            href="/"
            className="inline-block rounded-lg bg-brand-green px-6 py-3 text-white font-medium hover:bg-brand-green-hover transition-colors"
          >
            Try Again
          </a>
        </div>
      </div>
    );
  }

  return <Spinner overlay />;
}
